// src/admin/components/reports/OccupancyReport.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  RiBuildingLine,
  RiBarChartBoxLine,
  RiAlertLine,
  RiRefreshLine,
  RiArrowLeftLine,
  RiCalendarLine,
  RiMoneyDollarCircleLine,
  RiDownloadLine,
  RiPrinterLine
} from 'react-icons/ri';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTheme } from '../../../contexts/ThemeContext';
import { getOccupancyStats, formatCurrency, formatDate } from '../../../models/reportModel';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import i18n from '../../../i18n';

const OccupancyReport = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [date, setDate] = useState(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  // Check if RTL language
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  // RTL styles
  const rtlStyles = {
    direction: isRTL ? 'rtl' : 'ltr',
    textAlign: isRTL ? 'right' : 'left'
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getOccupancyStats(date);
        setData(response);
      } catch (err) {
        console.error(t('error_fetching_occupancy'), err);
        setError(err.message || t('failed_load_occupancy'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [date]);

  const handleDateSelection = (date) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      setDate(`${year}-${month}-${day}`);
    }
  };

  const handleRefresh = () => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getOccupancyStats(date);
        setData(response);
      } catch (err) {
        console.error(t('error_fetching_occupancy'), err);
        setError(err.message || t('failed_load_occupancy'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  };

  // Prepare chart data
  const chartData = data ? [
    { name: t('occupied'), value: data.summary.occupiedApartments },
    { name: t('vacant'), value: data.summary.vacantApartments },
    { name: t('maintenance'), value: data.summary.maintenanceApartments }
  ] : [];

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b'];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const percentage = data
        ? ((payload[0].value / data.summary.totalApartments) * 100).toFixed(1)
        : 0;

      return (
        <div className={`p-3 rounded-lg shadow-lg ${isDarkMode ? 'bg-zinc-900 text-white' : 'bg-white text-gray-900'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className="font-medium">{payload[0].name}</p>
          <p className="text-sm mt-1">
            {payload[0].value} {t('apartments')} ({percentage}%)
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'} style={rtlStyles}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="text-start">
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <RiBuildingLine className={`me-3 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            {t('occupancy_report')}
          </h1>
          <p className={`mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
            {formatDate(date)}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <DatePicker
              selected={date ? new Date(date) : null}
              onChange={handleDateSelection}
              dateFormat="MM/dd/yyyy"
              placeholderText="mm/dd/yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              isClearable
              portalId="root-portal"
              className={`w-full p-2.5 ps-10 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
            />
            <RiCalendarLine className={`absolute start-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'} pointer-events-none`} />
          </div>

          <button
            onClick={handleRefresh}
            className={`px-3 py-2 rounded-lg ${isDarkMode
              ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } flex items-center`}
            disabled={loading}
          >
            <RiRefreshLine className={`me-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? t('loading') : t('refresh')}
          </button>

          <Link
            to="/admin/reports"
            className={`px-3 py-2 rounded-lg ${isDarkMode
              ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } flex items-center`}
          >
            <RiArrowLeftLine className={`me-2 ${isRTL ? 'rotate-180' : ''}`} />
            {t('back')}
          </Link>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-red-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'
          } border flex items-center`}>
          <RiAlertLine className="me-2 text-xl" />
          {error}
        </div>
      )}

      {/* Report Content */}
      {!loading && !error && data && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div className="text-start">
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('occupancy_rate')}
                  </p>
                  <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {data.summary.overallOccupancyRate}%
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {data.summary.occupiedApartments} {t('of')} {data.summary.totalApartments} {t('apartments')}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${data.summary.overallOccupancyRate > 90
                  ? isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'
                  : data.summary.overallOccupancyRate > 75
                    ? isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-100 text-red-600'
                    : isDarkMode ? 'bg-orange-900/20 text-orange-400' : 'bg-orange-100 text-orange-600'
                  }`}>
                  <RiBuildingLine size={24} />
                </div>
              </div>
            </div>

            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div className="text-start">
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('vacant_apartments')}
                  </p>
                  <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {data.summary.vacantApartments}
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {Math.round((data.summary.vacantApartments / data.summary.totalApartments) * 100)}% {t('of_total')}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${isDarkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-600'
                  }`}>
                  <RiBuildingLine size={24} />
                </div>
              </div>
            </div>

            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div className="text-start">
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('maintenance_apartments')}
                  </p>
                  <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {data.summary.maintenanceApartments}
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {Math.round((data.summary.maintenanceApartments / data.summary.totalApartments) * 100)}% {t('of_total')}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${isDarkMode ? 'bg-orange-900/20 text-orange-400' : 'bg-orange-100 text-orange-600'
                  }`}>
                  <RiBuildingLine size={24} />
                </div>
              </div>
            </div>

            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div className="text-start">
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('potential_income')}
                  </p>
                  <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(data.summary.potentialMonthlyIncome)}
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {data.summary.totalBuildings} {t('buildings')}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'
                  }`}>
                  <RiMoneyDollarCircleLine size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Occupancy Visualization */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('occupancy_distribution')}
              </h3>

              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    labelLine={false}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    wrapperStyle={{
                      color: isDarkMode ? '#e5e7eb' : '#1f2937'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className={`lg:col-span-2 p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('occupancy_by_building')}
              </h3>

              <div className="overflow-x-auto">
                <table className={`w-full text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <thead className={`text-xs uppercase ${isDarkMode ? 'text-zinc-400 bg-zinc-800' : 'text-gray-500 bg-gray-100'}`}>
                    <tr>
                      <th scope="col" className="px-4 py-3 text-start">{t('building')}</th>
                      <th scope="col" className="px-4 py-3 text-end">{t('total_apartments')}</th>
                      <th scope="col" className="px-4 py-3 text-end">{t('occupied')}</th>
                      <th scope="col" className="px-4 py-3 text-end">{t('vacant')}</th>
                      <th scope="col" className="px-4 py-3 text-end">{t('occupancy_rate')}</th>
                      <th scope="col" className="px-4 py-3 text-end">{t('potential_income')}</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {data.occupancyStats.map(building => (
                      <tr
                        key={building.buildingId}
                        className={`${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-50'
                          } transition-colors duration-150`}
                      >
                        <td className="px-4 py-3 font-medium text-start">{building.buildingName}</td>
                        <td className="px-4 py-3 text-end">{building.total_apartments}</td>
                        <td className="px-4 py-3 text-end">{building.occupied_apartments}</td>
                        <td className="px-4 py-3 text-end">{building.vacant_apartments}</td>
                        <td className="px-4 py-3 text-end">
                          <div className="flex items-center justify-end">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${building.occupancy_rate > 90
                              ? isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-700'
                              : building.occupancy_rate > 75
                                ? isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-100 text-red-700'
                                : isDarkMode ? 'bg-orange-900/20 text-orange-400' : 'bg-orange-100 text-orange-700'
                              }`}>
                              {building.occupancy_rate}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-end">{formatCurrency(building.potential_monthly_income)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 px-1 mt-6">
            <button
              onClick={() => window.print()}
              className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } flex items-center`}
            >
              <RiPrinterLine className="me-2" />
              {t('print_report')}
            </button>

            <button
              className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-zinc-500 text-white hover:bg-red-600'
                } flex items-center`}
            >
              <RiDownloadLine className="me-2" />
              {t('export_report')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default OccupancyReport;